// Iter 82.4 / Phase E2.0 — Attendance Shifts admin.
// CRUD for shift definitions. Each shift owns: timing, grace minutes,
// per-day hour thresholds (full / half / absent), weekly off pattern,
// breaks (with paid/unpaid flag for net-working-time math), idle
// tracking switch, and an `is_default` invariant (only one default).
// E2.1 consumers read these as the authoritative source for late /
// early / half-day / absent classification.
import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Clock, Moon } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY_SHIFT = {
    code: "", name: "", description: "",
    start_time: "09:00", end_time: "18:00",
    grace_late_minutes: 15, grace_early_minutes: 15,
    full_day_hours: 8.0, half_day_hours_threshold: 4.0, absent_hours_threshold: 1.0,
    weekly_off_pattern: ["Sat", "Sun"],
    breaks: [],
    idle_tracking_enabled: false,
    is_active: true, is_default: false,
    approved_for_expected_hours: false,
};

export default function AttendanceShifts() {
    const { can } = useAuth();
    const [meta, setMeta] = useState({ weekdays: [], source_types: [], attendance_statuses: [] });
    const [shifts, setShifts] = useState([]);
    const [editRow, setEditRow] = useState(null);
    const [draft, setDraft] = useState(EMPTY_SHIFT);
    const [open, setOpen] = useState(false);

    const reload = useCallback(async () => {
        try {
            const [m, s] = await Promise.all([
                api.get("/attendance/masters/meta"),
                api.get("/attendance/shift/list"),
            ]);
            setMeta(m.data);
            setShifts(s.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load shifts"));
        }
    }, []);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);

    const openCreate = () => { setEditRow(null); setDraft(EMPTY_SHIFT); setOpen(true); };
    const openEdit = (row) => { setEditRow(row); setDraft({ ...EMPTY_SHIFT, ...row }); setOpen(true); };
    const close = () => { setOpen(false); setEditRow(null); setDraft(EMPTY_SHIFT); };

    const save = async () => {
        try {
            const body = { ...draft, code: (draft.code || "").trim() };
            // Inline validation — surface friendly errors before round-tripping to a generic 422.
            const blank = (body.breaks || []).findIndex((b) => !(b.name || "").trim());
            if (blank !== -1) {
                toast.error(`Break #${blank + 1} needs a name.`);
                return;
            }
            if (editRow) {
                await api.patch(`/attendance/shift/${editRow.id}`, body);
                toast.success("Shift updated.");
            } else {
                await api.post("/attendance/shift", body);
                toast.success("Shift created.");
            }
            close();
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete shift ${row.code} — ${row.name}?`)) return;
        try {
            await api.delete(`/attendance/shift/${row.id}`);
            toast.success("Deleted.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const toggleWeeklyOff = (day) => {
        const cur = draft.weekly_off_pattern || [];
        setDraft({
            ...draft,
            weekly_off_pattern: cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day],
        });
    };

    const updateBreak = (idx, patch) => {
        const next = [...(draft.breaks || [])];
        next[idx] = { ...next[idx], ...patch };
        setDraft({ ...draft, breaks: next });
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="attendance-shifts-page">
            <PageHeader
                eyebrow="Attendance Configuration · E2.0"
                title="Shifts"
                description="Define shift timing, grace minutes, full-day / half-day / absent thresholds, weekly off pattern, and breaks. These values drive E2.1 attendance classification (Late · Early Departure · Half Day · Absent)."
                actions={can("employee:admin") && (
                    <Button onClick={openCreate} data-testid="shifts-new-btn"><Plus size={14} className="mr-1" /> New Shift</Button>
                )}
            />

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="shifts-section">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Code</th>
                            <th className="text-left px-3 py-2 font-medium">Name</th>
                            <th className="text-left px-3 py-2 font-medium">Timing</th>
                            <th className="text-left px-3 py-2 font-medium">Thresholds (Full · ½ · Absent)</th>
                            <th className="text-left px-3 py-2 font-medium">Weekly Off</th>
                            <th className="text-left px-3 py-2 font-medium">Flags</th>
                            <th className="w-24" />
                        </tr>
                    </thead>
                    <tbody>
                        {shifts.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-slate-400">No shifts configured. Create your first shift to begin E2 attendance tracking.</td></tr>
                        )}
                        {shifts.map((s) => (
                            <tr key={s.id} className="border-t border-slate-100" data-testid={`shift-row-${s.id}`}>
                                <td className="px-3 py-2 font-mono text-xs">{s.code}</td>
                                <td className="px-3 py-2 text-slate-700">{s.name}</td>
                                <td className="px-3 py-2 text-xs text-slate-600">
                                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {s.start_time} → {s.end_time}</span>
                                    {s.is_overnight && <Badge className="ml-2 bg-indigo-100 text-indigo-700"><Moon size={10} className="mr-1" />Overnight</Badge>}
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{s.full_day_hours}h · {s.half_day_hours_threshold}h · {s.absent_hours_threshold}h</td>
                                <td className="px-3 py-2 text-xs text-slate-600">{(s.weekly_off_pattern || []).join(", ") || "—"}</td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="flex flex-wrap gap-1">
                                        {s.is_default && <Badge className="bg-emerald-100 text-emerald-700">Default</Badge>}
                                        {s.idle_tracking_enabled && <Badge className="bg-amber-100 text-amber-700">Idle</Badge>}
                                        {s.approved_for_expected_hours && <Badge className="bg-sky-100 text-sky-700" data-testid={`shift-approved-badge-${s.id}`}>Approved for expected-hours</Badge>}
                                        {!s.is_active && <Badge variant="secondary">Inactive</Badge>}
                                        {(s.breaks || []).length > 0 && <Badge variant="outline">{s.breaks.length} break(s)</Badge>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {can("employee:admin") && (
                                        <>
                                            <button onClick={() => openEdit(s)} className="text-slate-500 hover:text-slate-700 mr-2" data-testid={`shift-edit-${s.id}`}><PencilSimple size={14} /></button>
                                            <button onClick={() => remove(s)} className="text-rose-500 hover:text-rose-700" data-testid={`shift-delete-${s.id}`}><Trash size={14} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="shift-dialog">
                    <DialogHeader>
                        <DialogTitle>{editRow ? "Edit Shift" : "New Shift"}</DialogTitle>
                        <DialogDescription>Configure timing, thresholds, weekly off, and breaks. Overnight is auto-detected when end-time precedes start-time.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Code</Label><Input data-testid="shift-code" value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="GEN-DAY" /></div>
                            <div><Label className="text-xs">Name</Label><Input data-testid="shift-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="General Day Shift" /></div>
                        </div>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-3">
                            <legend className="text-xs font-semibold px-1">Timing &amp; Grace</legend>
                            <div className="grid grid-cols-4 gap-3">
                                <div><Label className="text-xs">Start (HH:MM)</Label><Input data-testid="shift-start" value={draft.start_time || ""} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} placeholder="09:00" /></div>
                                <div><Label className="text-xs">End (HH:MM)</Label><Input data-testid="shift-end" value={draft.end_time || ""} onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} placeholder="18:00" /></div>
                                <div><Label className="text-xs">Grace late (min)</Label><Input type="number" data-testid="shift-grace-late" value={draft.grace_late_minutes} onChange={(e) => setDraft({ ...draft, grace_late_minutes: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Grace early (min)</Label><Input type="number" data-testid="shift-grace-early" value={draft.grace_early_minutes} onChange={(e) => setDraft({ ...draft, grace_early_minutes: parseInt(e.target.value || "0", 10) })} /></div>
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-3">
                            <legend className="text-xs font-semibold px-1">Hour Thresholds (E2.1 classifiers)</legend>
                            <div className="grid grid-cols-3 gap-3">
                                <div><Label className="text-xs">Full-day hours</Label><Input type="number" step="0.5" data-testid="shift-full-hrs" value={draft.full_day_hours} onChange={(e) => setDraft({ ...draft, full_day_hours: parseFloat(e.target.value) })} /></div>
                                <div><Label className="text-xs">Half-day threshold (h)</Label><Input type="number" step="0.5" data-testid="shift-half-hrs" value={draft.half_day_hours_threshold} onChange={(e) => setDraft({ ...draft, half_day_hours_threshold: parseFloat(e.target.value) })} /></div>
                                <div><Label className="text-xs">Absent if &lt; (h)</Label><Input type="number" step="0.5" data-testid="shift-absent-hrs" value={draft.absent_hours_threshold} onChange={(e) => setDraft({ ...draft, absent_hours_threshold: parseFloat(e.target.value) })} /></div>
                            </div>
                            <p className="text-[11px] text-slate-500">Worked hours &lt; absent threshold → Absent · &lt; half threshold → Half Day · ≥ full hours → Present.</p>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Weekly Off Pattern</legend>
                            <div className="flex flex-wrap gap-1.5" data-testid="shift-weekly-off">
                                {(meta.weekdays || []).map((d) => {
                                    const on = (draft.weekly_off_pattern || []).includes(d);
                                    return (
                                        <button
                                            key={d}
                                            type="button"
                                            onClick={() => toggleWeeklyOff(d)}
                                            data-testid={`weekly-off-${d}`}
                                            className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-rose-100 border-rose-300 text-rose-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                                        >
                                            {d}
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Breaks (Lunch · Tea · etc.)</legend>
                            {(draft.breaks || []).map((b, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded" data-testid={`break-row-${i}`}>
                                    <Input className="col-span-3 h-9 text-xs" placeholder="Name (Lunch)" value={b.name || ""} onChange={(e) => updateBreak(i, { name: e.target.value })} data-testid={`break-name-${i}`} />
                                    <Input className="col-span-3 h-9 text-xs" placeholder="Start HH:MM" value={b.start_time || ""} onChange={(e) => updateBreak(i, { start_time: e.target.value })} data-testid={`break-start-${i}`} />
                                    <Input className="col-span-2 h-9 text-xs" type="number" placeholder="Mins" value={b.duration_minutes || 0} onChange={(e) => updateBreak(i, { duration_minutes: parseInt(e.target.value || "0", 10) })} data-testid={`break-duration-${i}`} />
                                    <label className="col-span-3 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!b.is_paid} onChange={(e) => updateBreak(i, { is_paid: e.target.checked })} data-testid={`break-paid-${i}`} /> Paid</label>
                                    <button className="col-span-1 text-rose-500 hover:text-rose-700" onClick={() => setDraft({ ...draft, breaks: draft.breaks.filter((_, j) => j !== i) })} data-testid={`break-remove-${i}`}><Trash size={14} /></button>
                                </div>
                            ))}
                            <Button size="sm" variant="outline" data-testid="shift-add-break" onClick={() => setDraft({ ...draft, breaks: [...(draft.breaks || []), { name: "", start_time: "13:00", duration_minutes: 60, is_paid: false }] })}>
                                <Plus size={12} className="mr-1" /> Add Break
                            </Button>
                            <p className="text-[11px] text-slate-500">Unpaid breaks are deducted from net working time.</p>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Flags</legend>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="shift-default" checked={!!draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} /> Mark as Default (auto-flips any other default)</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="shift-idle" checked={!!draft.idle_tracking_enabled} onChange={(e) => setDraft({ ...draft, idle_tracking_enabled: e.target.checked })} /> Enable idle-time tracking (consumed by E2.1 aggregator)</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="shift-active" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
                            <label className="flex items-center gap-2 text-xs" title="Only when TRUE does the 3-Month My Attendance dashboard use this shift's full_day_hours as an expected-hours denominator. Does not affect check-in/out, payroll, leave, or existing reports."><input type="checkbox" data-testid="shift-approved-for-expected-hours" checked={!!draft.approved_for_expected_hours} onChange={(e) => setDraft({ ...draft, approved_for_expected_hours: e.target.checked })} /> Approved for expected-hours analytics (3-Month My Attendance dashboard only)</label>
                        </fieldset>

                        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={close}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="shift-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
